"use client";

import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Activity, Award, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[1, 2, 3, 4].map((_, i) => <li key={i} data-index={i} className="h-5 w-24 bg-muted/50 animate-pulse rounded-md" />)}
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="group flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary">
            <span className="h-1 w-1 rounded-full bg-primary/40 transition-all group-hover:w-2 group-hover:bg-primary"></span>
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const specialties = [{
    name: "Dr. Muaaz Tarabichi",
    path: "/DrMuaazTarabichiPage"
  }, {
    name: "Dr. Robert Vincent",
    path: "/DrRobertVincentPage"
  }, {
    name: "Otosclerosis Research",
    path: "/OtosclerosisPage"
  }, {
    name: "Referring Doctor Portal",
    path: "/ReferringDoctorsPage"
  }, {
    name: "Patient Portal Access",
    path: "/PatientPortalPage"
  }];
  return <section data-section-id="179405" id="footer" className="dark relative overflow-hidden border-t border-border/20 bg-background text-foreground">
      {}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
      <div className="pointer-events-none absolute left-1/2 top-0 h-px w-full max-w-4xl -translate-x-1/2 bg-gradient-to-r from-transparent via-primary/40 to-transparent"></div>

      <div className="relative mx-auto max-w-7xl px-4 pb-8 pt-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {}
          <div className="flex flex-col space-y-6">
            <Link to="/HomePage" className="inline-block w-fit">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1597413/img/neuroai_clinical_logo_dark.png" alt="Stapes Excellence Center" className="h-7" />
            </Link>
            <p className="text-sm leading-relaxed text-muted-foreground">
              A global partnership in surgical mastery, uniting world leaders to advance the treatment of otosclerosis and hearing restoration.
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              <Badge variant="outline" className="border-border/40 bg-muted/10 font-mono text-xs text-muted-foreground backdrop-blur-sm">
                <ShieldCheck className="mr-1.5 size-3.5 text-primary" />
                Secure Data
              </Badge>
              <Badge variant="outline" className="border-border/40 bg-muted/10 font-mono text-xs text-muted-foreground backdrop-blur-sm">
                <Award className="mr-1.5 size-3.5 text-primary" />
                Board Certified
              </Badge>
            </div>
          </div>

          {}
          <div>
            <h3 className="mb-6 font-mono text-xs font-semibold uppercase tracking-wider text-foreground/90">
              Patient Resources
            </h3>
            <MenuProvider menu_id="81">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div>
            <h3 className="mb-6 font-mono text-xs font-semibold uppercase tracking-wider text-foreground/90">
              Professional Excellence
            </h3>
            <ul className="space-y-3">
              {specialties.map((specialty, i) => <li key={i} data-index={i}>
                  <Link to={specialty.path} className="group flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary">
                    <span className="h-1 w-1 rounded-full bg-primary/40 transition-all group-hover:w-2 group-hover:bg-primary"></span>
                    {specialty.name}
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="flex flex-col space-y-6">
            <h3 className="mb-2 font-mono text-xs font-semibold uppercase tracking-wider text-foreground/90">
              Global Centers
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-muted-foreground">
                <MapPin className="mt-0.5 size-4 shrink-0 text-primary" />
                <span>Tarabichi Stammberger Ear and Sinus Institute, Dubai</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-phone size-4 shrink-0 text-primary" aria-hidden="true" data-ast-id="f:/src/reusable_sections/Footer.tsx|sig:w2x9wn" data-ast-start="5047" data-ast-end="5097"><path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"></path></svg><span data-ast-id="f:/src/reusable_sections/Footer.tsx|sig:gnwhiz" data-ast-start="5114" data-ast-end="5142">+971 4 4131000</span></li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <Mail className="size-4 shrink-0 text-primary" />
                <span>callcenter@tarabichi.org</span>
              </li>
            </ul>

            {}
            
          </div>
        </div>

        {}
        <Separator className="my-8 bg-border/30" />
        
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <div className="flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 font-mono text-xs text-primary/90 backdrop-blur-sm">
            <Activity className="size-3.5 animate-pulse text-chart-2 drop-shadow-[0_0_5px_rgba(var(--color-chart-2),0.8)]" />
            <span>Consultations: Available</span>
          </div>
          
          <div className="text-center text-sm text-muted-foreground md:text-left">
            © {new Date().getFullYear()} Stapes Excellence Center. All rights reserved.
          </div>
          
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="#" className="transition-colors hover:text-primary">
              Privacy Policy
            </Link>
            <Link to="#" className="transition-colors hover:text-primary">
              Legal Notice
            </Link>
          </div>
        </div>
      </div>
    </section>;
}